import React, { useEffect, useMemo, useState } from 'react';
import Ionicons from '@expo/vector-icons/Ionicons';
import { ActivityIndicator, Alert, StyleSheet, Text, TouchableOpacity } from './rn';
import { useTranslation } from '../i18n/I18nContext';
import { useTheme } from '../theme/ThemeContext';
import { fontSize, spacing, radius, type ThemeColors } from '../theme';
import { api, NetworkError } from '../services/api';
import { passkeysAvailable } from '../passkeys/authenticator';
import { signInWithPasskey } from '../passkeys/passkeyFlow';

/**
 * D-882 — "Sign in with passkey" on the sign-in screen: Face ID, Touch ID, a
 * fingerprint or the screen lock, whichever this device uses. Shown only where the
 * device can hold a passkey and the server has the sign-in switched on.
 */
export default function PasskeySignInAction({ onVerify, disabled, tryStart, onFinish }: {
  onVerify?: (token: string, onboarded: boolean) => void | Promise<void>;
  disabled?: boolean;
  /** Lets the screen keep one sign-in method running at a time. */
  tryStart?: () => boolean;
  onFinish?: () => void;
}) {
  const { t } = useTranslation();
  const colors = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const available = passkeysAvailable();

  useEffect(() => {
    if (!available) return undefined;
    let live = true;
    api.passkeySignInStatus()
      .then((status) => { if (live) setEnabled(status.enabled === true); })
      .catch(() => {});
    return () => { live = false; };
  }, [available]);

  if (!available || !enabled || !onVerify) return null;

  async function signIn() {
    if (busy || disabled || tryStart?.() === false) return;
    setBusy(true);
    try {
      const result = await signInWithPasskey({
        title: t('passkeys.tapTitle'), action: t('passkeys.tapAction'), cancel: t('common.cancel'),
      });
      if (result.ok) { await onVerify?.(result.token, result.onboarded); return; }
      if (result.reason === 'cancelled') return;
      Alert.alert(t('common.error'), t(result.reason === 'none' ? 'passkeys.signInNone' : 'passkeys.signInFailed'));
    } catch (error) {
      if (error instanceof NetworkError) Alert.alert(t('message.noConnection'), t('message.checkInternet'));
      else Alert.alert(t('common.error'), t('passkeys.signInFailed'));
    } finally {
      setBusy(false);
      onFinish?.();
    }
  }

  return (
    <TouchableOpacity testID="passkey-sign-in" accessibilityRole="button" accessibilityLabel={t('passkeys.signIn')}
      accessibilityState={{ busy, disabled: !!disabled || busy }} disabled={!!disabled || busy}
      onPress={signIn} style={[styles.button, (disabled || busy) && styles.dim]}>
      {busy ? <ActivityIndicator color={colors.black} /> : <>
        <Ionicons name="key" size={20} color={colors.black} accessible={false} />
        <Text style={styles.label}>{t('passkeys.signIn')}</Text>
      </>}
    </TouchableOpacity>
  );
}

const makeStyles = (colors: ThemeColors) => StyleSheet.create({
  button: {
    width: '100%', minHeight: 48, marginTop: spacing.md, flexDirection: 'row', alignItems: 'center',
    justifyContent: 'center', gap: spacing.sm, borderRadius: radius.control, borderWidth: 1,
    borderColor: colors.gray200, backgroundColor: colors.white,
  },
  label: { fontSize: fontSize.md, fontWeight: '600', color: colors.black },
  dim: { opacity: 0.5 },
});
